import React from 'react';
import { useSelector } from 'react-redux';

import { getLanguage, getMarket } from '@travel/i18n';
import cx from '@travel/icons/_utils/classnames';
import { ArrowRightThick } from '@travel/icons/ui';
import { useDeviceTypes, useTranslation } from '@travel/traveler-core/hooks';
import useRatBindClickComponent from '@travel/traveler-core/hooks/useRatBindClickComponent';
import { FlatButton } from '@travel/ui';

import { Translate } from 'core/translate';
import { LinkWithRef } from 'core/universalRouter/Link/LinkWithRef';

import styles from './campaignBanner.module.scss';

export type CampaignBannerProps = {
  hasButton?: boolean;
  url: string;
  imagePC: string;
  imageSP: string;
  title: string;
  subtitle: string;
  ctaText: string;
  onClick?: React.MouseEventHandler;
  size: 'default' | 'medium';
  imageStylePC?: {
    width: string;
    top: string;
    left: string;
  };
  imageStyleSP?: {
    width: string;
    top: string;
    left: string;
  };
  ratParams: {
    pageName: string;
    siteSection: string;
    pageType: string;
    queryParam: string;
  };
};

/**
 * Banner UI component
 * @param hasButton - Show button on desktop
 * @param url - URL to link to
 * @param imagePC - Image to use on desktop
 * @param imageSP - Image to use on mobile
 * @param title - Title to display
 * @param subtitle - Subtitle to display
 * @param ctaText - Text to display on button
 * @param onClick - Click handler
 * @param ratParams - Parameters to send to ratevent
 * @param size - Size of the banner
 * @param imageStylePC - Style for the image on desktop
 * @param imageStyleSP - Style for the image on mobile
 */
function CampaignBanner(props: CampaignBannerProps) {
  const {
    hasButton,
    url,
    imagePC,
    imageSP,
    title,
    subtitle,
    ctaText,
    onClick,
    ratParams,
    size,
    imageStylePC = {},
    imageStyleSP = {},
  } = props;
  const { isPC } = useDeviceTypes();
  const data = {
    marketcode: useSelector(getMarket)?.marketCode.toLowerCase(),
    languagesetting: useSelector(getLanguage).toLowerCase(),
  };
  const bannerLink =
    useTranslation({
      id: url,
      data: data,
    }) +
    '?' +
    ratParams.queryParam;

  const imageStyle = isPC ? imageStylePC : imageStyleSP;

  const ratLink = React.useRef<HTMLAnchorElement>(null);
  useRatBindClickComponent(ratLink);

  const classNames = cx(styles.campaignBanner, size === 'medium' && styles.campaignBannerMedium);

  return (
    <section data-testid="campaign-banner-wrapper">
      <LinkWithRef
        className={classNames}
        data-testid="campaign-banner-component"
        to={bannerLink}
        href={bannerLink}
        target="_blank"
        rel="noreferrer noopener"
        onClick={onClick}
        data-ratevent="click"
        data-rat-pgn={ratParams.pageName}
        data-rat-ssc={ratParams.siteSection}
        data-rat-pgt={ratParams.pageType}
        ref={ratLink}
      >
        <div className={cx(styles.campaignImg)}>
          <img style={imageStyle} src={isPC ? imagePC : imageSP} alt="" />
        </div>
        <div className={cx(styles.campaignContent)}>
          <div className={cx(styles.title)}>
            <Translate id={title} />
          </div>
          <div className={cx(styles.subtitle)}>
            <Translate id={subtitle} />
          </div>
        </div>
        {isPC && hasButton && (
          <div>
            <FlatButton className={styles.exploreRentalButton} data-testid="campaign-banner-button">
              <Translate id={ctaText} />
            </FlatButton>
          </div>
        )}
        {!isPC && <ArrowRightThick size={16} color="cilantro" />}
      </LinkWithRef>
    </section>
  );
}

export default CampaignBanner;
